// One index's page, `stores/<store>/indexes/<index>` (02-DESIGN/10-state/
// 01-stores-types-and-instances.md § the console: Stores; 03-indexes.md;
// 01-the-console.md § the address; hq decision 0069): its kind, what it reads
// and which changes, and *Delete* in the page's menu, asked first. An index is
// declared and deleted, never edited: changing one is deleting and declaring
// it. The crumb goes back to the store's Settings tab, where it is declared.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { Empty, NotAllowed, PageHead, Pending, Pill, useLoad } from "../../ui/components.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { RowMenu } from "../../ui/table.tsx";
import { IndexSource, validName, validStoreName } from "../../wire/state.ts";
import { useSurface } from "../surface.tsx";
import { SETTINGS_TAB, storeAddress } from "./addresses.ts";
import { INDEX_DELETE } from "./StorePage.tsx";

export function IndexPage() {
  const { store = "", index = "" } = useParams();
  const s = useSurface();
  if (!validStoreName(store) || !validName(index)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "stores")}>Stores</Link>}
        title="Not an index"
        line={`"${index}" is not an index's name in "${store}".`}
      />
    );
  }
  return <One key={`${store}/${index}`} store={store} index={index} />;
}

function One({ store, index }: { store: string; index: string }) {
  const s = useSurface();
  const navigate = useNavigate();
  const [indexes] = useLoad(`store/${store}/indexes/page/${index}`, () => s.bus.stores().listIndexes(store));
  const [err, setErr] = useState<unknown>(undefined);
  const [removing, setRemoving] = useState(false);
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "stores")}>Stores</Link>
      <span>/</span>
      <Link to={storeAddress(s.tenant, store)} state={{ tab: SETTINGS_TAB }} className="mono">
        {store}
      </Link>
      <span>/</span>
      <span className="mono">{index}</span>
    </>
  );
  if (indexes.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the index" />
      </>
    );
  }
  if (indexes.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={index} />
        <NotAllowed err={indexes.err} what="This index" />
      </>
    );
  }
  const ix = indexes.value.find((x) => x.name === index);
  if (ix === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={index} />
        <Empty>No index by this name in {store}.</Empty>
      </>
    );
  }
  const remove = () => {
    setErr(undefined);
    setRemoving(true);
    s.bus
      .stores()
      .deleteIndex(store, index)
      .then(
        () => {
          void navigate(storeAddress(s.tenant, store), { state: { tab: SETTINGS_TAB } });
        },
        (e: unknown) => {
          setRemoving(false);
          setErr(e);
        },
      );
  };
  const reads = ix.config.source === IndexSource.history ? "the changes" : "current state";
  return (
    <>
      <PageHead
        crumb={crumb}
        title={index}
        line={
          <>
            a {ix.kind} index in <NameLink to={storeAddress(s.tenant, store)}>{store}</NameLink>, reading{" "}
            {reads}
          </>
        }
        action={
          removing ? undefined : (
            <RowMenu
              label={`Actions for ${index}`}
              entries={[
                {
                  label: "Delete",
                  danger: true,
                  onSelect: remove,
                  confirm: { title: `Delete index ${index}?`, consequence: INDEX_DELETE },
                },
              ]}
            />
          )
        }
      />
      {err !== undefined && <NotAllowed err={err} />}
      {removing && <Pending what="Deleting the index" />}
      <section className="card">
        <dl className="kv">
          <dt>Kind</dt>
          <dd>
            <Pill>{ix.kind}</Pill>
          </dd>
          <dt>Reads</dt>
          <dd>{reads}</dd>
          <dt>Changes</dt>
          <dd>
            {ix.config.types !== undefined && ix.config.types.length > 0 ? (
              <span className="mono">{ix.config.types.join(", ")}</span>
            ) : (
              <span className="faint">every type</span>
            )}
          </dd>
        </dl>
        <p className="small muted" style={{ marginTop: 16 }}>
          An index is derived from the store&apos;s history: it is declared and deleted, never edited. To
          change it, delete it and declare it again.
        </p>
      </section>
    </>
  );
}
